import { Page } from 'components/page/page';
import './track-voucher.scss';
import { Card } from 'components/card/card';
import { useEffect, useRef, useState } from 'react';
import { TrackVoucherResponse } from 'core/domain/track-voucher/track-voucher';
import { getUtopiaFilters, getVoucherInfo } from 'core/repositories/track-voucher';
import { Input } from 'components/input/input';
import { Button, buttonType } from 'components/button/button';
import moment from 'jalali-moment';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { Enum } from 'core/domain';
import { digitSperator, faNumToEnNum } from 'core/utils/decimalExtentions';
import { CopyOutlined } from '@ant-design/icons';
import { copyToClipboard } from 'core/utils/copyToClipboard';
import { Dropdown } from 'components/dropdown/dropdown';
import { Descriptions, Typography } from 'antd';
import { jFormat } from 'core/utils/jFormat';

interface FilterValues {
    filter: Enum;
    from: string;
    to: string;
    keyword: string;
}

const empty_filters: FilterValues = {
    filter: {
        id: -1,
        title: 'همه فیلتر ها',
    },
    from: '',
    to: '',
    keyword: '',
};

export const TrackVoucher = () => {
    const [voucherInfo, setVoucherInfo] = useState<TrackVoucherResponse>();
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [filterValues, setFilterValues] = useState<FilterValues>(empty_filters);
    const [filters, setFilters] = useState<Enum[]>([empty_filters.filter]);
    const [query, setQuery] = useState('');

    const fromRef = useRef(null);
    const toRef = useRef(null);

    const filterHandle = (clearFilter?: boolean) => {
        let _query = '';

        if (filterValues.filter.id !== -1)
            _query += `${_query.includes('?') ? '&' : '?'}filter=${filterValues.filter.id}`;
        if (filterValues.from !== '')
            _query += `${_query.includes('?') ? '&' : '?'}from=${jFormat(filterValues.from)}`;
        if (filterValues.to !== '')
            _query += `${_query.includes('?') ? '&' : '?'}to=${jFormat(filterValues.to)}`;

        setQuery(_query);
        getVoucherInfo(
            clearFilter ? setDeleteLoading : setLoading,
            setVoucherInfo,
            filterValues.keyword,
            clearFilter ? '' : _query,
        );
    };

    const clearFiltersHandle = () => setFilterValues(empty_filters);

    useEffect(() => {
        getUtopiaFilters(setFiltersLoading, setFilters);
    }, []);

    return (
        <Page className="pmx-track-voucher" title="استعلام ووچر یوتوپیا">
            <Card className="voucher-tracking" title="استعلام ووچر یوتوپیا">
                <div className="filter-row">
                    <div className="fields">
                        <Input
                            value={filterValues.keyword}
                            placeholder="کد ووچر"
                            onChange={(e) => setFilterValues({ ...filterValues, keyword: e.target.value })}
                            onPressEnter={filterHandle}
                        />
                        {filters.length > 0 && (
                            <Dropdown
                                items={filters}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        filter: filters.find((l) => l.id === value),
                                    })
                                }
                                selectedOption={filterValues.filter}
                            />
                        )}
                    </div>
                </div>
                <div className="filter-row">
                    <div className="fields">
                        <DatePicker
                            className="date-picker"
                            value={filterValues.from}
                            calendar={persian}
                            ref={fromRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input value={filterValues.from} icon={<CalendarIcon />} placeholder="از تاریخ" />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({ ...filterValues, from: e.format() });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.to}
                            calendar={persian}
                            ref={toRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input value={filterValues.to} icon={<CalendarIcon />} placeholder="تا تاریخ" />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({ ...filterValues, to: e.format() });
                            }}
                        />
                        <div className="buttons">
                            <Button
                                loading={loading}
                                disabled={loading}
                                text="جستجو"
                                type={buttonType.success}
                                onClick={() => filterHandle()}
                            />
                            <Button
                                loading={deleteLoading}
                                disabled={deleteLoading}
                                text="حذف فیلتر"
                                type={buttonType.error}
                                onClick={clearFiltersHandle}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {voucherInfo ? (
                <Card title="اطلاعات ووچر" className="voucher-info">
                    <section>
                        <Descriptions className="voucher-desc" colon={false} column={1}>
                            <Descriptions.Item label="اکانت فعال شده:">
                                <Typography.Text
                                    className="en"
                                    ellipsis={{ tooltip: voucherInfo?.payeeAccount || '-' }}
                                    copyable={
                                        voucherInfo?.payeeAccount
                                            ? {
                                                text: voucherInfo.payeeAccount,
                                                icon: [<CopyOutlined />, <CopyOutlined />],
                                            }
                                            : false
                                    }
                                >
                                    {voucherInfo?.payeeAccount || '-'}
                                </Typography.Text>
                            </Descriptions.Item>

                            <Descriptions.Item label="بچ:">
                                <Typography.Text
                                    className="en"
                                    ellipsis={{ tooltip: voucherInfo?.batch || '-' }}
                                    copyable={
                                        voucherInfo?.batch
                                            ? {
                                                text: voucherInfo.batch,
                                                icon: [<CopyOutlined />, <CopyOutlined />],
                                            }
                                            : false
                                    }
                                >
                                    {voucherInfo?.batch || '-'}
                                </Typography.Text>
                            </Descriptions.Item>

                            <Descriptions.Item label="تاریخ فعالسازی:">
                                {(() => {
                                    const isValid = moment(voucherInfo?.activatedDateTime).isValid();
                                    const value = isValid
                                        ? faNumToEnNum(
                                            moment
                                                .utc(voucherInfo.activatedDateTime)
                                                .locale('fa')
                                                .local()
                                                .format('HH:mm:ss - jYYYY/jMM/jDD'),
                                        )
                                        : '-';
                                    return (
                                        <Typography.Text
                                            ellipsis={{ tooltip: value }}
                                            copyable={
                                                isValid
                                                    ? {
                                                        text: value,
                                                        icon: [<CopyOutlined />, <CopyOutlined />],
                                                    }
                                                    : false
                                            }
                                        >
                                            {value}
                                        </Typography.Text>
                                    );
                                })()}
                            </Descriptions.Item>

                            <Descriptions.Item label="مقدار:">
                                <Typography.Text
                                    className="en"
                                    ellipsis={{ tooltip: String(digitSperator(voucherInfo?.amount) || '-') }}
                                >
                                    {digitSperator(voucherInfo?.amount) || '-'}
                                </Typography.Text>
                            </Descriptions.Item>

                            <Descriptions.Item label="کد ووچر:">
                                <Typography.Text
                                    className="en"
                                    ellipsis={{ tooltip: voucherInfo?.voucherCode || '-' }}
                                >
                                    {voucherInfo?.voucherCode || '-'}
                                </Typography.Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </section>
                </Card>
            ) : null}
        </Page>
    );
};